import type { DeploymentEnvironment, JobSchedule, JobScheduleListResponse } from '@dev-console/types';
import { Pod } from '@shared/pods/constants';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ToggleScheduleDialog } from '@/components/job-schedules/toggle-schedule-dialog';
import {
  JobsDbEnvironmentBadge,
  JobsDbEnvironmentToggle,
  jobsDbEnvironmentParamDef,
} from '@/components/jobs/jobs-db-environment-toggle';
import { EmptyState, ErrorBanner, LoadingCard } from '@/components/ui/state-views';
import { getJobSchedules } from '@/config/api-helpers';
import { useSearchParamsState } from '@/hooks/useSearchParamsState';
import { recordVisit } from '@/hooks/useVisitHistory';
import { useAuth } from '@/lib/auth-context';

export const meta = {
  label: 'Job Schedules',
  order: 14,
  section: 'Jobs',
};

const SCHEDULES_REFRESH_INTERVAL_MS = 30_000;
const PODS: (Pod | '')[] = ['', ...Object.values(Pod)];

const INCIDENT_LEAD_GROUP = 'SG-Eng Incident Lead';

// Mirrors assertCanSetJobScheduleEnabled in the server's jobSchedulesController;
// the server remains the enforcement point.
function canToggleSchedule(user: { pods?: string[]; groups?: string[] } | null, schedule: JobSchedule): boolean {
  if (user == null) return false;
  const pods = user.pods ?? [];
  return (
    pods.includes(Pod.PLATFORM) || (user.groups ?? []).includes(INCIDENT_LEAD_GROUP) || pods.includes(schedule.pod)
  );
}

type EnabledFilter = 'all' | 'enabled' | 'disabled';

const paramDefs = {
  environment: jobsDbEnvironmentParamDef,
  jobName: { default: '' },
  pod: { default: '' },
  enabled: {
    default: 'all' as EnabledFilter,
    parse: (raw: string): EnabledFilter => (raw === 'enabled' || raw === 'disabled' ? raw : 'all'),
  },
};

function formatRelative(iso: string): string {
  const deltaMs = new Date(iso).getTime() - Date.now();
  const absoluteMinutes = Math.round(Math.abs(deltaMs) / 60_000);

  if (absoluteMinutes < 1) return deltaMs >= 0 ? 'in <1m' : '<1m ago';

  const value =
    absoluteMinutes < 60
      ? `${absoluteMinutes}m`
      : absoluteMinutes < 1_440
      ? `${Math.round(absoluteMinutes / 60)}h`
      : `${Math.round(absoluteMinutes / 1_440)}d`;

  return deltaMs >= 0 ? `in ${value}` : `${value} ago`;
}

/**
 * The scheduler advances nextRunAt as soon as it materializes an occurrence, so an enabled
 * schedule sitting in the past means the scheduler is not keeping up.
 */
function isScheduleBehind(schedule: JobSchedule): boolean {
  return schedule.enabled && new Date(schedule.nextRunAt).getTime() <= Date.now();
}

function buildPostgresJobsLink(jobName: string, environment: DeploymentEnvironment): string {
  const sp = new URLSearchParams({ name: jobName });
  if (environment !== jobsDbEnvironmentParamDef.default) sp.set('environment', environment);
  return `/postgres-jobs?${sp}`;
}

function ScheduleRow({
  schedule,
  environment,
  canToggle,
  onToggle,
}: {
  schedule: JobSchedule;
  environment: DeploymentEnvironment;
  canToggle: boolean;
  onToggle: (schedule: JobSchedule) => void;
}) {
  const behind = isScheduleBehind(schedule);

  return (
    <tr className='border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50'>
      <td className='px-3 py-2.5 font-mono text-xs text-neutral-800'>
        <Link to={buildPostgresJobsLink(schedule.jobName, environment)} className='text-blue-600 hover:underline'>
          {schedule.jobName}
        </Link>
      </td>
      <td className='px-3 py-2.5'>
        <span
          className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
            schedule.enabled ? 'bg-green-100 text-green-800' : 'bg-neutral-100 text-neutral-600'
          }`}
        >
          {schedule.enabled ? 'Enabled' : 'Disabled'}
        </span>
      </td>
      <td className='px-3 py-2.5 font-mono text-xs text-neutral-700'>{schedule.cronExpression}</td>
      <td className='px-3 py-2.5 text-neutral-600'>{schedule.pod}</td>
      <td className='px-3 py-2.5 text-neutral-600'>{schedule.lane}</td>
      <td className='whitespace-nowrap px-3 py-2.5 text-xs text-neutral-600'>
        <div className={behind ? 'font-medium text-amber-700' : ''}>{formatRelative(schedule.nextRunAt)}</div>
        <div className='text-neutral-400'>{new Date(schedule.nextRunAt).toLocaleString()}</div>
      </td>
      <td className='whitespace-nowrap px-3 py-2.5 text-xs text-neutral-600'>
        {schedule.lastRunAt == null ? (
          <span className='text-neutral-400'>Never</span>
        ) : (
          <>
            <div>{formatRelative(schedule.lastRunAt)}</div>
            <div className='text-neutral-400'>{new Date(schedule.lastRunAt).toLocaleString()}</div>
          </>
        )}
      </td>
      <td className='px-3 py-2.5'>
        <button
          type='button'
          disabled={!canToggle}
          onClick={() => onToggle(schedule)}
          title={canToggle ? undefined : `Requires the ${schedule.pod} pod, Platform pod, or SG-Eng Incident Lead`}
          className={`rounded-md border px-2.5 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50 ${
            schedule.enabled
              ? 'border-red-200 text-red-700 hover:bg-red-50 disabled:hover:bg-white'
              : 'border-neutral-200 text-neutral-700 hover:bg-neutral-50 disabled:hover:bg-white'
          }`}
        >
          {schedule.enabled ? 'Disable' : 'Enable'}
        </button>
      </td>
    </tr>
  );
}

export const JobSchedulesPage = () => {
  const {
    environment: [environment, setEnvironment],
    jobName: [jobNameFilter, setJobNameFilter],
    pod: [podFilter, setPodFilter],
    enabled: [enabledFilter, setEnabledFilter],
  } = useSearchParamsState(paramDefs);

  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [toggleTarget, setToggleTarget] = useState<JobSchedule | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  useEffect(() => {
    recordVisit('/job-schedules', 'Job Schedules', 'job');
  }, []);

  const {
    data,
    error,
    isLoading,
    refetch: refetchSchedules,
  } = useQuery({
    queryKey: ['job-schedules', environment],
    queryFn: () => getJobSchedules(environment),
    refetchInterval: SCHEDULES_REFRESH_INTERVAL_MS,
  });

  const schedules = useMemo(() => {
    const search = jobNameFilter.trim().toLowerCase();

    return (data?.schedules ?? []).filter((schedule) => {
      if (enabledFilter === 'enabled' && !schedule.enabled) return false;
      if (enabledFilter === 'disabled' && schedule.enabled) return false;
      if (podFilter !== '' && schedule.pod !== podFilter) return false;
      return search === '' || schedule.jobName.toLowerCase().includes(search);
    });
  }, [data?.schedules, enabledFilter, jobNameFilter, podFilter]);

  const behindCount = useMemo(() => schedules.filter(isScheduleBehind).length, [schedules]);

  const handleEnvironmentChange = (next: DeploymentEnvironment) => {
    setEnvironment(next);
    setToggleTarget(null);
    setToggleError(null);
  };

  return (
    <div>
      <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-2'>
          <h2 className='font-mediumbold text-xl text-neutral-900'>Job Schedules</h2>
          <JobsDbEnvironmentBadge environment={environment} />
        </div>
        <JobsDbEnvironmentToggle value={environment} onChange={handleEnvironmentChange} />
      </div>

      <div className='mb-4 flex flex-wrap items-center gap-3'>
        <input
          type='text'
          value={jobNameFilter}
          onChange={(e) => setJobNameFilter(e.target.value)}
          placeholder='Search job name...'
          className='h-9 w-64 rounded-md border border-neutral-300 px-3 text-sm text-neutral-800 placeholder-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
        />
        <select
          value={enabledFilter}
          onChange={(e) => setEnabledFilter(e.target.value as EnabledFilter)}
          className='h-9 rounded-md border border-neutral-300 bg-white px-3 text-sm text-neutral-700'
        >
          <option value='all'>All schedules</option>
          <option value='enabled'>Enabled only</option>
          <option value='disabled'>Disabled only</option>
        </select>
        <select
          aria-label='Pod'
          value={podFilter}
          onChange={(e) => setPodFilter(e.target.value)}
          className='h-9 rounded-md border border-neutral-300 bg-white px-3 text-sm text-neutral-700'
        >
          {PODS.map((pod) => (
            <option key={pod} value={pod}>
              {pod || 'All pods'}
            </option>
          ))}
        </select>
        {behindCount > 0 && (
          <span className='text-sm font-medium text-amber-700'>{behindCount} schedule(s) past their next run time</span>
        )}
      </div>

      {error != null && (
        <ErrorBanner
          className='mb-4'
          message={error instanceof Error ? error.message : 'Failed to load job schedules'}
          onRetry={refetchSchedules}
        />
      )}

      {toggleError != null && <ErrorBanner className='mb-4' message={toggleError} />}

      {isLoading && <LoadingCard message='Loading job schedules...' />}

      {!isLoading && error == null && schedules.length === 0 && <EmptyState message='No job schedules found.' />}

      {schedules.length > 0 && (
        <div className='overflow-x-auto rounded-md border border-neutral-200'>
          <table className='w-full text-left text-sm'>
            <thead className='border-b border-neutral-200 bg-neutral-50'>
              <tr>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Job</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Status</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Cron (UTC)</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Pod</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Lane</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Next Run</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Last Run</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((schedule) => (
                <ScheduleRow
                  key={schedule.id}
                  schedule={schedule}
                  environment={environment}
                  canToggle={canToggleSchedule(user, schedule)}
                  onToggle={setToggleTarget}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toggleTarget != null && (
        <ToggleScheduleDialog
          schedule={toggleTarget}
          environment={environment}
          onClose={() => setToggleTarget(null)}
          onSuccess={(updated) => {
            setToggleTarget(null);
            setToggleError(null);
            queryClient.setQueryData<JobScheduleListResponse>(['job-schedules', environment], (current) =>
              current == null
                ? current
                : { schedules: current.schedules.map((s) => (s.id === updated.id ? updated : s)) }
            );
          }}
          onError={(err) => {
            setToggleError(err instanceof Error ? err.message : 'Failed to update job schedule');
          }}
        />
      )}
    </div>
  );
};
